import styled, { keyframes, css } from 'styled-components'
import { rem, transitions } from 'polished'
import { GatsbyImage } from 'gatsby-plugin-image'

import theme from 'styles/theme'
import easing from 'styles/easing'
import { Title60 } from 'components/Typography'

export const Wrapper = styled.div`
  color: ${(props) => props.theme.colors.sagedarkToblack};
  margin: 0;

  & + & {
    padding-top: ${rem(16)};
  }

  & + & h2 {
    display: none;
  }

  & *[data-gatsby-image-wrapper] {
    display: none;
  }

  &:has(+ &),
  &:not(& + &) {
    *[data-gatsby-image-wrapper] {
      display: block;
    }
  }

  ${(props) => props.theme.mediaQueries.medium} {
    *[data-gatsby-image-wrapper] {
      display: block !important;
    }

    & + & {
      padding-top: ${rem(80)};
    }
  }

  ${({ alternateVersion, noBorderTop }) =>
    !alternateVersion &&
    !noBorderTop &&
    css`
      &::before {
        border-top: 1px solid ${(props) => props.theme.colors.sagelight};
        content: '';
        display: block;
        margin: 0 auto;
        width: calc(100% - 80px);

        ${theme.mediaQueries.large} {
          max-width: 780px;
          width: 66%;
        }
      }
    `}
`
export const NoImgContainer = styled.div`
  ${theme.mediaQueries.large} {
    position: relative;
    margin: 0 auto;
    max-width: ${rem(1200)};
  }

  ${theme.mediaQueries.medium} {
    position: relative;
    margin: 0 auto;
    max-width: ${rem(780)};
  }
`

export const Heading = styled(Title60)`
  text-align: center;
  padding-bottom: ${rem(50)};
`

export const List = styled.ul`
  display: block;
  margin: 0;
  padding: 0;

  ${({ alternateVersion }) =>
    alternateVersion &&
    css`
      & > *:last-child {
        border-bottom: 1px solid
          ${(props) => props.theme.colors.alabasterToGreen};
      }
    `}
`

export const ListItem = styled.li`
  border-top: 1px solid ${(props) => props.theme.colors.alabasterToGreen};
  display: block;
  margin: 0;
  padding: 0;
`

export const ItemHeading = styled.button`
  appearance: none;
  background: none;
  border: 0 none;
  border-radius: 0;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  outline: none;
  padding: 24px 20px;
  text-align: inherit;
  width: 100%;

  &::after {
    border: 1px solid;
    border-width: 0 1px 1px 0;
    content: '';
    display: block;
    height: 9px;
    transform: translateY(12px) rotate(45deg);
    width: 9px;
  }

  span {
    flex: 1 1 100%;
    margin-right: 15px;
  }

  &[aria-expanded='true'] {
    &::after {
      transform: translateY(16px) rotate(-135deg);
    }
  }

  ${theme.mediaQueries.large} {
    padding: 22px 20px;
  }

  ${({ alternateVersion }) =>
    alternateVersion &&
    css`
      padding: ${rem(18)} ${rem(26)};

      & > * {
        font-size: ${rem(16)};
      }

      &::after {
        ${transitions('transform 0.3s', 'width 0.3s', 'height 0.3s')}
        transform: translateY(2px) translateX(-4px) rotate(0deg);
      }

      &::before {
        ${transitions('transform 0.3s', 'width 0.3s', 'height 0.3s')}
        content: '';
        display: block;
        border: 1px solid;
        border-width: 0 1px 1px 0;
        height: 9px;
        width: 9px;
        right: ${rem(20)};
        position: absolute;
        transform: translateY(10px) translateX(-2px) rotate(180deg);
      }

      &[aria-expanded='true'] {
        &::after {
          transform: translateY(10px) translateX(4px) rotate(0deg);
        }

        &::before {
          transform: translateY(10px) translateX(-2px) rotate(180deg);
        }

        &::after,
        &::before {
          height: 0px;
          width: 18px;
        }
      }

      ${(props) => props.theme.mediaQueries.medium} {
        span {
          line-height: 110%;
        }

        & > * {
          font-size: ${rem(20)};
        }

        &::before {
          transform: translateY(14px) translateX(-2px) rotate(180deg);
        }

        &::after {
          transform: translateY(6px) translateX(-4px) rotate(0deg);
        }

        &[aria-expanded='true'] {
          &::after {
            transform: translateY(14px) translateX(4px) rotate(0deg);
          }

          &::before {
            transform: translateY(14px) translateX(-2px) rotate(180deg);
          }
        }
      }

      ${(props) => props.theme.mediaQueries.large} {
        padding: ${rem(18)} ${rem(26)};
      }
    `}
`

const CopyOpen = keyframes`
  0% { height: 0 }
`

const CopyClose = keyframes`
  100% { height: 0 }
`

export const Copy = styled.div`
  animation: ${CopyOpen} 0.3s ${easing.easeOutQuart};
  overflow: hidden;
  transition: visibility 0.3s;
  &[aria-hidden='true'] {
    animation: ${CopyClose} 0.3s ${easing.easeOutQuart} forwards;
    height: 0;
    visibility: hidden;
  }
`

export const CopyInner = styled.div`
  h2,
  h4,
  h5 {
    color: ${(props) => props.theme.colors.sagedarkToblack};
  }
  padding: 0 20px 14px;

  ${({ alternateVersion }) =>
    alternateVersion &&
    css`
      p,
      span,
      bold,
      i,
      div {
        font-size: ${rem(12)};
      }
    `}
`

export const ItemCta = styled.button`
  ${transitions(
    'width 0.3s',
    'color 0.3s',
    'border 0.3s',
    'background-color 0.3s'
  )}
  width: calc(100% - 90px);
  display: block;
  margin: 0 auto ${rem(26)} auto;
  padding: ${rem(17)} 0px;
  border: 1px solid transparent;
  background-color: ${(props) => props.theme.colors.sagedarkToGreen};
  color: ${(props) => props.theme.colors.whiteToSandstone};
  font-size: ${rem(12)};
  font-family: ${(props) => props.theme.textStyles.brownStd};

  &:hover,
  &:active {
    background-color: ${(props) => props.theme.colors.whiteToSandstone};
    color: ${(props) => props.theme.colors.sagedarkToGreen};
    border-color: ${(props) => props.theme.colors.sagedarkToGreen};
  }

  ${(props) => props.theme.mediaQueries.medium} {
    width: auto;
    padding: ${rem(16)} ${rem(50)};
    font-size: ${rem(14)};
    line-height: 150%;
  }
`

export const AccordionCta = styled(ItemCta)`
  margin-top: ${rem(24)};
  margin-bottom: 0px;
`

export const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  row-gap: ${rem(16)};

  ${(props) => props.theme.mediaQueries.medium} {
    flex-flow: row nowrap;
    row-gap: initial;
    column-gap: ${rem(47)};
    max-width: ${rem(975)};
    margin: 0 auto;

    &[data-direction='Left'] {
      flex-direction row-reverse;
    }
 
    &[data-direction='Right'] {
      flex-direction row;
    }

    & > * {
      max-width: 50%;
    }
  }
`

export const Content = styled.div`
  position: relative;
`

export const ImageContainer = styled.div`
  position: relative;
  flex: 1;
`

export const Image = styled(GatsbyImage)`
  width: 100%;

  img {
    object-fit: contain !important;
    object-position: center center;
  }

  ${(props) => props.theme.mediaQueries.medium} {
    width: ${rem(470)};
    height: ${rem(300)};

    img {
      object-fit: cover !important;
      object-position: center center;
    }
  }
`
